import axios from 'axios';
import { useEspacioStore } from '../store/espacio';
import { rutaEnEspacio } from '../utils/espacio';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// "Ver como": si hay un espacio compartido activo, las peticiones que su rol puede
// hacer llevan X-Espacio (el servidor lo valida contra una membresía activa).
api.interceptors.request.use((config) => {
  const espacio = useEspacioStore.getState().espacio;
  if (espacio && rutaEnEspacio(espacio.rol, config.method, config.url)) {
    config.headers['X-Espacio'] = String(espacio.id);
  }
  return config;
});

// Interceptor para manejar respuestas
api.interceptors.response.use(
  (response) => {
    if (response.data && response.data.data !== undefined) {
      if (response.data.meta !== undefined) {
        response.meta = response.data.meta;
      }
      response.data = response.data.data;
    }
    return response;
  },
  (error) => {
    // skipAuthRedirect: la petición es opcional para visitantes (p. ej. /planes
    // consulta el plan actual solo si hay sesión) y un 401 no debe mandar a /login
    if (error.response?.status === 401 && !error.config?.skipAuthRedirect && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    const body = error.response?.data?.data ?? error.response?.data ?? {};
    // Desanidar aquí, igual que el interceptor de éxito hace con response.data.data,
    // para que err.response.data.error funcione en toda la app. Sin esto, cada
    // pantalla leía err.response.data.error contra el objeto todavía envuelto en
    // {data: {...}} y siempre caía al mensaje genérico de su catch, ocultando el
    // motivo real que mandaba el backend (límite del plan, validación, etc.).
    if (error.response) error.response.data = body;
    // Emitir evento global cuando el plan está al límite
    if (body?.upgrade === true) {
      window.dispatchEvent(new CustomEvent('upgrade-required', { detail: { error: body.error } }));
    }
    // Modo mantenimiento
    if (error.response?.status === 503 && body?.mantenimiento === true) {
      window.dispatchEvent(new CustomEvent('modo-mantenimiento', { detail: { mensaje: body.error } }));
    }
    return Promise.reject(error);
  }
);

// Servicios de autenticación
export const authService = {
  login: (usuario, contrasena, totp_code) =>
    api.post('/login', { usuario, contrasena, ...(totp_code ? { totp_code } : {}) }),
  logout: () => api.post('/logout'),
  getMe: () => api.get('/me', { validateStatus: (s) => s === 200 || s === 401 }),
};

// Servicios financieros
export const finanzasService = {
  getResumen: () => api.get('/finanzas/resumen'),
  getIngresos: () => api.get('/finanzas/ingresos'),
  getGastos: () => api.get('/finanzas/gastos'),
  getBalance: () => api.get('/finanzas/balance'),
  getTendencia: (dias = 30) => api.get(`/finanzas/tendencia?dias=${dias}`),
  getProyeccion: (meses = 3) => api.get(`/finanzas/proyeccion?meses=${meses}`),
  getMetas: () => api.get('/finanzas/metas'),
  getSalud: () => api.get('/finanzas/salud'),
  getGastosAnomalos: () => api.get('/finanzas/gastos-anomalos'),
  getTransacciones: (limite = 10) => api.get(`/finanzas/transacciones?limite=${limite}`),
  getDineroDisponible: () => api.get('/finanzas/disponible-hoy'),
  getMotor: () => api.get('/finanzas/motor'),
  getComparativa: () => api.get('/finanzas/comparativa'),
  getPuedoGastar: () => api.get('/finanzas/puedo-gastar'),
};

// Retención: "Tu semana", alertas de cargos inusuales y opt-out del resumen semanal
export const retencionService = {
  getSemana: (semana) => api.get('/semana', { params: semana ? { s: semana } : {} }),
  getAlertas: () => api.get('/alertas'),
  marcarNormal: (id) => api.post(`/alertas/${id}/normal`),
  descartarAlerta: (id) => api.post(`/alertas/${id}/descartar`),
  agregarSuscripcion: (id) => api.post(`/alertas/${id}/suscripcion`),
  getResumenSemanal: () => api.get('/perfil/resumen-semanal'),
  setResumenSemanal: (activo) => api.put('/perfil/resumen-semanal', { activo }),
  getCorreosMarketing: () => api.get('/perfil/correos-marketing'),
  setCorreosMarketing: (activo) => api.put('/perfil/correos-marketing', { activo }),
};

// Insignias por hábitos sanos (Free y Pro)
export const insigniasService = {
  get: () => api.get('/insignias'),
  marcarVistas: () => api.post('/insignias/vistas'),
};

// Servicios de transacciones (UNIFICADOS - FASE 2)
export const transaccionesService = {
  // Nuevos métodos unificados
  buscar: (filtros = {}) => {
    const params = new URLSearchParams();
    if (filtros.fecha_inicio) params.append('fecha_inicio', filtros.fecha_inicio);
    if (filtros.fecha_fin) params.append('fecha_fin', filtros.fecha_fin);
    if (filtros.tipo) params.append('tipo', filtros.tipo);
    if (filtros.categoria_id) params.append('categoria_id', filtros.categoria_id);
    if (filtros.cuenta_id) params.append('cuenta_id', filtros.cuenta_id);
    if (filtros.search) params.append('search', filtros.search);
    if (filtros.limit) params.append('limit', filtros.limit);
    if (filtros.offset) params.append('offset', filtros.offset);

    return api.get(`/transacciones?${params}`);
  },

  obtenerFiltros: () => api.get('/transacciones/filtros'),

  crear: (data) => api.post('/transacciones', data),

  actualizar: (id, data) => api.put(`/transacciones/${id}`, data),

  eliminar: (id) => api.delete(`/transacciones/${id}`),

  // Wrappers para compatibilidad con código existente
  getIngresos: (filters = {}) => transaccionesService.buscar({ ...filters, tipo: 'ingreso' }),

  getGastos: (filters = {}) => transaccionesService.buscar({ ...filters, tipo: 'gasto' }),

  crearIngreso: (data) => transaccionesService.crear({ ...data, tipo: 'ingreso' }),

  crearGasto: (data) => transaccionesService.crear({ ...data, tipo: 'gasto' }),

  actualizarIngreso: (id, data) => transaccionesService.actualizar(id, data),

  actualizarGasto: (id, data) => transaccionesService.actualizar(id, data),

  eliminarIngreso: (id) => transaccionesService.eliminar(id),

  eliminarGasto: (id) => transaccionesService.eliminar(id),

  // `extra` (opcional): cuenta_id | tarjeta_credito_id | crear, archivo, pagos_tarjeta (importación por lote)
  importarBulk: (transacciones, extra = {}) => api.post("/transacciones/bulk", { transacciones, ...extra }),
  importarEstadoCuenta: (archivo) => {
    const formData = new FormData();
    formData.append('archivo', archivo);
    return api.post('/importar/pdf', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 150000 });
  },
};

// Servicios de ingresos automáticos
export const ingresosAutomaticosService = {
  getIngresos: () => api.get('/ingresos-automaticos'),
  crear: (data) => api.post('/ingresos-automaticos', data),
  actualizar: (id, data) => api.put(`/ingresos-automaticos/${id}`, data),
  eliminar: (id) => api.delete(`/ingresos-automaticos/${id}`),
};

// Servicios de metas
export const metasService = {
  getMetas: () => api.get('/finanzas/metas'),
  crearMeta: (data) => api.post('/finanzas/metas', data),
  actualizarMeta: (id, data) => api.put(`/finanzas/metas/${id}`, data),
  eliminarMeta: (id) => api.delete(`/finanzas/metas/${id}`),
  abonarMeta: (id, monto, cuenta_id) => api.post(`/finanzas/metas/${id}/abonar`, { monto, cuenta_id }),
};

// Servicios de perfil
export const perfilService = {
  actualizarPerfil: (data) => api.put('/perfil', data),
  cambiarContrasena: (actual, nueva) =>
    api.post('/perfil/cambiar-contrasena', { actual, nueva }),
  subirFoto: (archivo) => {
    const formData = new FormData();
    formData.append('foto', archivo);
    return api.post('/perfil/foto', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Servicios de reportes
export const reportesService = {
  generar: (tipo = 'pdf') => api.get(`/reportes/generar?tipo=${tipo}`),
};

// Servicios de cuentas
export const cuentasService = {
  getCuentas: () => api.get('/cuentas'),
  crearCuenta: (data) => api.post('/cuentas', data),
  eliminarCuenta: (id) => api.delete(`/cuentas/${id}`),
};

// Servicios de tarjetas de crédito
export const tarjetasService = {
  getTarjetas: () => api.get('/tarjetas-credito'),
  crearTarjeta: (data) => api.post('/tarjetas-credito', data),
  actualizarTarjeta: (id, data) => api.put(`/tarjetas-credito/${id}`, data),
  eliminarTarjeta: (id) => api.delete(`/tarjetas-credito/${id}`),
  getGastosTarjeta: (id) => api.get(`/tarjetas-credito/${id}/gastos`),
  getHealth: (id) => api.get(`/tarjetas-credito/${id}/health`),
  getPendingTransactions: (id) => api.get(`/tarjetas-credito/${id}/pending`),
  recordPayment: (id, data) => api.post(`/tarjetas-credito/${id}/pago`, data),
  getInstallments: (id) => api.get(`/tarjetas-credito/${id}/cuotas`),
  createInstallment: (id, data) => api.post(`/tarjetas-credito/${id}/cuotas`, data),
  deleteInstallment: (id, msiId) => api.delete(`/tarjetas-credito/${id}/cuotas/${msiId}`),
};

// Servicios de API Keys (para Shortcuts)
export const apiKeysService = {
  getApiKeys: () => api.get('/api-keys'),
  crearApiKey: (nombre) => api.post('/api-keys', { nombre }),
  revocarApiKey: (id) => api.delete(`/api-keys/${id}`),
};

// Atajos de iPhone: estado (llaves, última vez usada, tarjetas detectadas), asociación de tarjetas y pendientes por revisar
export const atajosService = {
  estado: () => api.get('/atajos/estado'),
  asociarTarjeta: (id, destino) => api.put(`/atajos/tarjetas/${id}`, destino),
  conservar: (id) => api.put(`/atajos/revisar/${id}`),
};

// Servicios para Shortcuts (gastos/ingresos rápidos, auth por API Key)
export const shortcutsService = {
  crearGastoRapido: (monto, descripcion, apiKey) => {
    const config = { headers: { 'X-API-Key': apiKey, 'Content-Type': 'application/json' } };
    return axios.post(`${API_BASE_URL}/gasto-rapido`, { monto, descripcion }, config);
  },
  crearIngresoRapido: (monto, descripcion, apiKey) => {
    const config = { headers: { 'X-API-Key': apiKey, 'Content-Type': 'application/json' } };
    return axios.post(`${API_BASE_URL}/ingreso-rapido`, { monto, descripcion }, config);
  },
};

// Servicios de catálogos
export const catalogService = {
  getCatalogs: () => api.get('/catalogs'),
};

// Servicios de categorías personalizadas
export const categoriesService = {
  getCategories: (tipo = null) => {
    const url = tipo ? `/categorias?tipo=${tipo}` : '/categorias';
    return api.get(url);
  },
  createCategory: (nombre, tipo = 'gasto') =>
    api.post('/categorias', { nombre, tipo }),
  deleteCategory: (id) =>
    api.delete(`/categorias/${id}`),
  ordenarPropuesta: () => api.get('/categorias/ordenar/propuesta', { skipAuthRedirect: true }),
  ordenarAplicar: (mapeo) => api.post('/categorias/ordenar/aplicar', { mapeo }),
  api,
};

// Servicios de insights automáticos
export const insightsService = {
  getInsights: () => api.get('/insights'),
};

// Servicios de presupuestos
export const presupuestosService = {
  getPresupuestos: (mes, anio) => api.get(`/presupuestos?mes=${mes}&anio=${anio}`),
  getProgreso: (mes, anio, periodo = 'mensual') =>
    api.get(`/presupuestos/progreso?mes=${mes}&anio=${anio}${periodo !== 'mensual' ? `&periodo=${periodo}` : ''}`),
  getSobres: (mes, anio, periodo = 'mensual') =>
    api.get(`/presupuestos/sobres?mes=${mes}&anio=${anio}&periodo=${periodo}`),
  setPeriodo: (data) => api.put('/presupuestos/periodo', data),
  crearPresupuesto: (data) => api.post('/presupuestos', data),
  actualizarPresupuesto: (id, data) => api.put(`/presupuestos/${id}`, data),
  eliminarPresupuesto: (id) => api.delete(`/presupuestos/${id}`),
};

// Servicios de deudas
export const deudasService = {
  getDeudas: () => api.get('/deudas'),
  crearDeuda: (data) => api.post('/deudas', data),
  actualizarDeuda: (id, data) => api.put(`/deudas/${id}`, data),
  eliminarDeuda: (id) => api.delete(`/deudas/${id}`),
  registrarPago: (id, data) => api.post(`/deudas/${id}/pago`, data),
};

// Servicios de ML - Auto-categorización
export const suscripcionesService = {
  getResumen:   () => api.get('/suscripciones/resumen'),
  getAll:       () => api.get('/suscripciones'),
  detectar:     () => api.get('/suscripciones/detectar'),
  crear:        (data) => api.post('/suscripciones', data),
  actualizar:   (id, data) => api.put(`/suscripciones/${id}`, data),
  eliminar:     (id) => api.delete(`/suscripciones/${id}`),
};

// Gastos e ingresos compartidos entre usuarios
export const compartidosService = {
  getMios:       () => api.get('/compartidos'),
  getConmigo:    () => api.get('/compartidos/conmigo'),
  crear:         (data) => api.post('/compartidos', data),
  actualizar:    (id, data) => api.put(`/compartidos/${id}`, data),
  eliminar:      (id) => api.delete(`/compartidos/${id}`),
  marcarPagado:  (partId, pagado) => api.put(`/compartidos/participantes/${partId}/pagar`, { pagado }),
  aceptar:       (partId, data) => api.put(`/compartidos/participantes/${partId}/aceptar`, data),
  rechazar:      (partId) => api.put(`/compartidos/participantes/${partId}/rechazar`),
  // Solo coincidencia exacta del correo (ver handleBuscarUsuario en el backend)
  buscarUsuario: (email) => api.get(`/usuarios/buscar?email=${encodeURIComponent(email)}`),
};

// Proyectos freelance
export const proyectosService = {
  getAll:       () => api.get('/proyectos'),
  crear:        (data) => api.post('/proyectos', data),
  actualizar:   (id, data) => api.put(`/proyectos/${id}`, data),
  eliminar:     (id) => api.delete(`/proyectos/${id}`),
  marcarPagado: (partId, pagado) => api.put(`/proyectos/participantes/${partId}/pagar`, { pagado }),
  // Pasar lo que te toca a una de tus cuentas como ingreso real (y deshacerlo)
  registrarCobro:            (id, data) => api.post(`/proyectos/${id}/cobro`, data),
  deshacerCobro:             (id) => api.delete(`/proyectos/${id}/cobro`),
  registrarCobroParticipante: (partId, data) => api.post(`/proyectos/participantes/${partId}/cobro`, data),
  deshacerCobroParticipante:  (partId) => api.delete(`/proyectos/participantes/${partId}/cobro`),
};

export const mlService = {
  predictCategory: (texto, tipo) => api.post('/predict-category', {
    texto,
    tipo
  }),
};

export default api;

// Espacios compartidos (Hogar / Contador)
export const espaciosService = {
  listar: () => api.get('/espacios'),
  invitar: (identificador, rol) => api.post('/espacios/invitar', { identificador, rol }),
  responder: (id, accion) => api.put(`/espacios/${id}/${accion}`),
  revocar: (id) => api.delete(`/espacios/${id}`),
};

// Inversiones
export const inversionesService = {
  listar: () => api.get('/inversiones'),
  crear: (data) => api.post('/inversiones', data),
  actualizar: (id, data) => api.put(`/inversiones/${id}`, data),
  eliminar: (id) => api.delete(`/inversiones/${id}`),
  valuar: (id, valor_actual) => api.post(`/inversiones/${id}/valuacion`, { valor_actual }),
  precioCripto: (id) => api.post(`/inversiones/${id}/precio-cripto`),
  tasaCetes: (plazo, fecha) => api.get('/inversiones/tasa-cetes', { params: { plazo, fecha } }),
};

// OCR de tickets (Pro): devuelve un gasto SUGERIDO, nunca guarda nada
export const ocrService = {
  ticket: (archivo) => {
    const fd = new FormData();
    fd.append('imagen', archivo);
    return api.post('/ocr/ticket', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 75000 });
  },
};
